import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  type ButtonProps,
} from "@chakra-ui/react";
import { usePromiseLoading } from "@duet/ui/hooks/usePromiseLoading";
import { Fragment, cloneElement, forwardRef, isValidElement } from "react";

export interface ConfirmModalProps {
  trigger?:
    | React.ReactElement<{ onClick: () => void; ref?: any }>
    | React.ComponentType<{ onClick: () => void }>;
  header?: React.ReactNode;
  content?: React.ReactNode;
  primaryAction?: ButtonProps;
  onConfirm: () => Promise<any> | any;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export const ConfirmModal = forwardRef(function ConfirmModal(
  {
    // casing matters for proper jsx compilation
    trigger: Trigger,
    header,
    content,
    onConfirm,
    primaryAction = {},
    isOpen: isOpenProp,
    onOpen: onOpenProp,
    onClose: onCloseProp,
  }: ConfirmModalProps,
  ref
) {
  const { isOpen, onOpen, onClose } = useDisclosure({
    isOpen: isOpenProp,
    onClose: onCloseProp,
    onOpen: onOpenProp,
  });
  const [_onConfirm, isLoading] = usePromiseLoading(onConfirm, [onConfirm]);
  const _trigger = isValidElement(Trigger)
    ? cloneElement(Trigger, {
        onClick: onOpen,
        ref,
      })
    : Trigger && <Trigger onClick={onOpen} />;

  return (
    <Fragment>
      {_trigger}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>{header ?? "Confirm"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>{content ?? "Confirm action"}</ModalBody>
          <ModalFooter>
            <ButtonGroup>
              {!isLoading && (
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              )}
              <Button
                {...makeBtnProps(primaryAction)}
                isLoading={isLoading}
                onClick={async () => {
                  await _onConfirm();
                  onClose();
                }}
              />
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Fragment>
  );
});

function makeBtnProps(props: ButtonProps) {
  if (!props.content && !props.children) {
    props.children = "Confirm";
  }

  if (props.content) {
    props.children = props.content;
  }

  if (!props.colorScheme && !props.variant) {
    props.variant = "primary";
  }

  return props;
}
